import { VALIDATION_PATTERNS } from '../../../../constants/validationPatterns';
import { TYPE_OF_PURCHASE } from '../../../CreateBooking/constants/DetailedBookingConstants';
import {
    ContextualNodeFields,
    DetailedBookingSOVPartialData
} from '../../../CreateBooking/interface/DetailedBookingInterface';

interface ContextualNodesSOVProps extends ContextualNodeFields {
    contextualNode: string;
}

interface ValidateSOVFieldsProps {
    formData: DetailedBookingSOVPartialData & { typeOfPurchase?: string },
    contextualNodes: ContextualNodesSOVProps[],
    t: (key: string) => string,
    isContextualSelected: boolean
}

const getSOVFormErrors = (formData: DetailedBookingSOVPartialData & { typeOfPurchase?: string }, t: (key: string) => string, isContextualSelected: boolean) => {
    return {
        entryNumber: '',
        gpSlot: !formData.gpSlot.trim() ? t('gp-slot-required-error') : '',
        // For contextual selections, sovPercentageForGP is not required as percentages are entered per contextual node
        sovPercentageForGP: isContextualSelected
            ? ''
            : (formData.sovPercentageForGP === undefined || formData.sovPercentageForGP === null || formData.sovPercentageForGP === 0)
                ? t('sov-percentage-for-gp-required-error')
                : (formData.sovPercentageForGP < 0)
                    ? t('sov-percentage-for-gp-invalid-error')
                    : '',
        // For GP SOV purchases, valueBlockedByLineItem is auto-calculated so don't require manual input
        valueBlockedByLineItem: formData.typeOfPurchase === TYPE_OF_PURCHASE.GP_SOV
            ? ''
            : (!formData.valueBlockedByLineItem)
                ? t('value-blocked-by-line-item-required-error')
                : (isNaN(formData.valueBlockedByLineItem) || formData.valueBlockedByLineItem < 0)
                    ? t('value-blocked-by-line-item-invalid-error')
                    : '',
        contextualNode: '',
        sovPercentageForContextualGP: '',
        impressionBooked: '',
        valueBooked: '',
    };
}

const getSOVFormContextualNodeErrors = (contextualNodes: ContextualNodesSOVProps[], t: (key: string) => string, isContextualSelected: boolean, isGPSOVContextual: boolean) => {
    return isContextualSelected
        ? contextualNodes.map((node) => ({
            contextualNode: !node.contextualNode.trim()
                ? t('contextual-node-contextual-node-required-error')
                : '',
            sovPercentageForContextualGP: !node.sovPercentageForContextualGP
                ? t('contextual-node-sov-percentage-required-error')
                : !VALIDATION_PATTERNS.PERCENTAGE.test(
                    node.sovPercentageForContextualGP.toString()
                )
                    ? t('contextual-node-sov-percentage-invalid-error')
                    : '',
            // For GP SOV contextual selections, impressionBooked and valueBooked are auto-calculated, hence skip validation
            impressionBooked: isGPSOVContextual
                ? ''
                : !node.impressionBooked
                    ? t('contextual-node-impression-booked-required-error')
                    : !VALIDATION_PATTERNS.NUMERIC.test(node.impressionBooked.toString())
                        ? t('contextual-node-impression-booked-invalid-error')
                        : '',
            valueBooked: isGPSOVContextual
                ? ''
                : !node.valueBooked
                    ? t('contextual-node-value-booked-required-error')
                    : !VALIDATION_PATTERNS.NUMERIC.test(node.valueBooked.toString())
                        ? t('contextual-node-value-booked-invalid-error')
                        : '',
        }))
        : [];  // Return empty array for non-contextual selections
}

export const validateDetailedBookingSOVFields = ({ formData, contextualNodes, t, isContextualSelected }: ValidateSOVFieldsProps) => {
    const errors = getSOVFormErrors(formData, t, isContextualSelected);
    const isGPSOVContextual = formData.typeOfPurchase === TYPE_OF_PURCHASE.GP_SOV && isContextualSelected;
    const contextualNodeErrors = getSOVFormContextualNodeErrors(contextualNodes, t, isContextualSelected, isGPSOVContextual);

    return { mainFormErrors: errors, contextualNodeErrors };
};